import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Polyline, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';

// @ts-ignore
import icon from 'leaflet/dist/images/marker-icon.png';
// @ts-ignore
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
});

const createStartIcon = (color: string, text: string) => L.divIcon({
    className: 'custom-marker',
    html: `<div style="display: flex; flex-direction: column; align-items: center;">
        <div style="background-color: ${color}; color: white; padding: 4px 8px; border-radius: 4px; font-size: 11px; font-weight: bold; box-shadow: 0 2px 5px rgba(0,0,0,0.3); white-space: nowrap;">${text}</div>
        <div style="width: 0; height: 0; border-left: 8px solid transparent; border-right: 8px solid transparent; border-top: 10px solid ${color};"></div>
    </div>`,
    iconSize: [50, 40],
    iconAnchor: [25, 40],
});

const createEndIcon = (color: string, text: string) => L.divIcon({
    className: 'custom-marker',
    html: `<div style="display: flex; flex-direction: column; align-items: center;">
        <div style="background-color: ${color}; color: white; padding: 4px 8px; border-radius: 4px; font-size: 11px; font-weight: bold; box-shadow: 0 2px 5px rgba(0,0,0,0.3); white-space: nowrap;">${text}</div>
        <div style="width: 0; height: 0; border-left: 8px solid transparent; border-right: 8px solid transparent; border-top: 10px solid ${color};"></div>
    </div>`,
    iconSize: [50, 40],
    iconAnchor: [25, 40],
});

const createStartEndIcon = (color: string, text: string) => L.divIcon({
    className: 'custom-marker',
    html: `<div style="display: flex; flex-direction: column; align-items: center;">
        <div style="background-color: ${color}; color: white; padding: 4px 8px; border-radius: 4px; font-size: 11px; font-weight: bold; box-shadow: 0 2px 5px rgba(0,0,0,0.3); white-space: nowrap;">${text}</div>
        <div style="width: 0; height: 0; border-left: 8px solid transparent; border-right: 8px solid transparent; border-top: 10px solid ${color};"></div>
    </div>`,
    iconSize: [70, 40],
    iconAnchor: [35, 40],
});

L.Marker.prototype.options.icon = DefaultIcon;

const MapContainerAny = MapContainer as any;
const TileLayerAny = TileLayer as any;
const PolylineAny = Polyline as any;
const MarkerAny = Marker as any;

interface RouteComparisonModalProps {
    actualRoutes: any[];
    plannedRoutes: any[];
    onClose: () => void;
}

export const RouteComparisonModal = ({ actualRoutes, plannedRoutes, onClose }: RouteComparisonModalProps) => {
    const { t } = useTranslation();

    // Procesar coordenadas de rutas reales
    const actualCoordinates = actualRoutes.flatMap((route: any) =>
        route.coordinates.map((coord: any) => [coord.latitude, coord.longitude])
    );

    // Procesar coordenadas de rutas planificadas
    const plannedCoordinates = plannedRoutes.flatMap((route: any) =>
        route.coordinates.map((coord: any) => [coord.lat, coord.lon])
    );

    // Calcular centro del mapa basado en las coordenadas reales
    const getCenter = (coordinates: any[]) => {
        if (coordinates.length === 0) return [40.4168, -3.7038]; // Madrid por defecto
        const lat = coordinates.reduce((sum: number, coord: any) => sum + coord[0], 0) / coordinates.length;
        const lon = coordinates.reduce((sum: number, coord: any) => sum + coord[1], 0) / coordinates.length;
        return [lat, lon];
    };

    const actualCenter = getCenter(actualCoordinates);
    const plannedCenter = getCenter(plannedCoordinates);

    // Calcular distancias y duraciones totales
    const totalActualDistance = actualRoutes.reduce((sum: number, route: any) => sum + (route.distance || 0), 0);
    const totalActualDuration = actualRoutes.reduce((sum: number, route: any) => sum + (route.duration || 0), 0);
    const totalPlannedDistance = plannedRoutes.reduce((sum: number, route: any) => sum + (route.distance || 0), 0);
    const totalPlannedDuration = plannedRoutes.reduce((sum: number, route: any) => sum + (route.duration || 0), 0);

    const formatDuration = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${hours}h ${minutes}m`;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-[95vw] h-[95vh] flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="text-xl font-bold">{t('common.label.comparacion-rutas')}</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 flex gap-4 p-4 overflow-hidden">
                    {/* Ruta Real */}
                    <div className="flex-1 flex flex-col">
                        <div className="mb-2">
                            <h3 className="text-lg font-semibold text-green-600">{t('common.label.ruta-real')}</h3>
                            <div className="text-sm text-gray-600">
                                <span className="mr-4">
                                    {t('common.label.distancia')}: {(totalActualDistance / 1000).toFixed(2)} km
                                </span>
                                <span>
                                    {t('common.label.duracion')}: {formatDuration(totalActualDuration)}
                                </span>
                            </div>
                        </div>
                        <div className="flex-1 rounded-lg overflow-hidden border">
                            {actualCoordinates.length > 0 ? (
                                <MapContainerAny
                                    center={actualCenter}
                                    zoom={13}
                                    scrollWheelZoom={true}
                                    style={{ height: '100%', width: '100%' }}
                                >
                                    <TileLayerAny
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    {actualRoutes.map((route: any, index: number) => {
                                        const coords = route.coordinates.map((coord: any) => [coord.latitude, coord.longitude]);
                                        if (coords.length > 0) {
                                            return (
                                                <PolylineAny
                                                    key={`actual-${index}`}
                                                    positions={coords}
                                                    color="#22c55e"
                                                    weight={4}
                                                    opacity={0.7}
                                                />
                                            );
                                        }
                                        return null;
                                    })}
                                    {/* Marcadores de inicio y fin */}
                                    {actualCoordinates.length > 0 && (() => {
                                        const start = actualCoordinates[0];
                                        const end = actualCoordinates[actualCoordinates.length - 1];
                                        // Usar tolerancia pequeña para comparar coordenadas (0.0001 grados ≈ 11 metros)
                                        const tolerance = 0.0001;
                                        const isSameLocation = 
                                            Math.abs(start[0] - end[0]) < tolerance && 
                                            Math.abs(start[1] - end[1]) < tolerance;
                                        
                                        console.log('Actual route - Start:', start, 'End:', end, 'Same location:', isSameLocation);
                                        
                                        if (isSameLocation) {
                                            return <MarkerAny key="actual-start-end" position={start} icon={createStartEndIcon('#22c55e', t('common.label.inicio') + '/' + t('common.form-label.final'))} />;
                                        } else {
                                            return (
                                                <>
                                                    <MarkerAny key="actual-start" position={start} icon={createStartIcon('#22c55e', t('common.label.inicio'))} />
                                                    <MarkerAny key="actual-end" position={end} icon={createEndIcon('#ef4444', t('common.form-label.final'))} />
                                                </>
                                            );
                                        }
                                    })()}
                                </MapContainerAny>
                            ) : (
                                <div className="h-full flex items-center justify-center text-gray-500">
                                    {t('common.label.no-data-available')}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Ruta Planificada */}
                    <div className="flex-1 flex flex-col">
                        <div className="mb-2">
                            <h3 className="text-lg font-semibold text-blue-600">{t('common.label.ruta-planificada')}</h3>
                            <div className="text-sm text-gray-600">
                                <span className="mr-4">
                                    {t('common.label.distancia')}: {(totalPlannedDistance / 1000).toFixed(2)} km
                                </span>
                                <span>
                                    {t('common.label.duracion')}: {formatDuration(totalPlannedDuration)}
                                </span>
                            </div>
                        </div>
                        <div className="flex-1 rounded-lg overflow-hidden border">
                            {plannedCoordinates.length > 0 ? (
                                <MapContainerAny
                                    center={plannedCenter}
                                    zoom={13}
                                    scrollWheelZoom={true}
                                    style={{ height: '100%', width: '100%' }}
                                >
                                    <TileLayerAny
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    {plannedRoutes.map((route: any, index: number) => {
                                        const coords = route.coordinates.map((coord: any) => [coord.lat, coord.lon]);
                                        if (coords.length > 0) {
                                            return (
                                                <PolylineAny
                                                    key={`planned-${index}`}
                                                    positions={coords}
                                                    color="#3b82f6"
                                                    weight={4}
                                                    opacity={0.7}
                                                />
                                            );
                                        }
                                        return null;
                                    })}
                                    {/* Marcadores de inicio y fin */}
                                    {plannedCoordinates.length > 0 && (() => {
                                        const start = plannedCoordinates[0];
                                        const end = plannedCoordinates[plannedCoordinates.length - 1];
                                        // Usar tolerancia pequeña para comparar coordenadas (0.0001 grados ≈ 11 metros)
                                        const tolerance = 0.0001;
                                        const isSameLocation = 
                                            Math.abs(start[0] - end[0]) < tolerance && 
                                            Math.abs(start[1] - end[1]) < tolerance;
                                        
                                        console.log('Planned route - Start:', start, 'End:', end, 'Same location:', isSameLocation);
                                        
                                        if (isSameLocation) {
                                            return <MarkerAny key="planned-start-end" position={start} icon={createStartEndIcon('#3b82f6', t('common.label.inicio') + '/' + t('common.form-label.final'))} />;
                                        } else {
                                            return (
                                                <>
                                                    <MarkerAny key="planned-start" position={start} icon={createStartIcon('#3b82f6', t('common.label.inicio'))} />
                                                    <MarkerAny key="planned-end" position={end} icon={createEndIcon('#ef4444', t('common.form-label.final'))} />
                                                </>
                                            );
                                        }
                                    })()}
                                </MapContainerAny>
                            ) : (
                                <div className="h-full flex items-center justify-center text-gray-500">
                                    {t('common.label.no-data-available')}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
